import { NavigateFunction } from 'react-router-dom'
import { AGENT_HIGHLIGHT_PARAM } from '../agent-highlight'

/** Append agentHighlight query for scroll/focus on destination (e.g. object-update pill click). */
export function withAgentHighlight(targetPath: string, focusKey?: string | null): string {
  if (!targetPath || !focusKey?.trim()) return targetPath
  try {
    const url = new URL(targetPath, window.location.origin)
    url.searchParams.set(AGENT_HIGHLIGHT_PARAM, focusKey.trim())
    return url.pathname + url.search + url.hash
  } catch {
    const [pathname, rest] = targetPath.split('?')
    const [query, hash] = rest ? rest.split('#') : ['', '']
    const params = new URLSearchParams(query)
    params.set(AGENT_HIGHLIGHT_PARAM, focusKey.trim())
    return pathname + '?' + params.toString() + (hash ? '#' + hash : '')
  }
}

/**
 * Navigate to a thread, preserving the current focus route and other query parameters.
 * 
 * @param navigate - React Router navigate function
 * @param threadId - The thread ID to navigate to
 * @param currentFocus - Current user focus (e.g., 'candidate', 'employer')
 * @param preserveParams - Whether to preserve other query parameters (default: false)
 * @param searchParams - Current URL search params to preserve (optional)
 */
export function navigateToThread(
  navigate: NavigateFunction,
  threadId: string,
  currentFocus: string | null = 'candidate',
  preserveParams: boolean = false,
  searchParams?: URLSearchParams
): void {
  const focus = currentFocus || 'candidate'
  
  // Build query parameters
  const params = new URLSearchParams()
  params.set('thread', threadId)
  
  // Preserve other query parameters if requested
  if (preserveParams && searchParams) {
    for (const [key, value] of searchParams.entries()) {
      if (key !== 'thread') {
        params.set(key, value)
      }
    }
  }
  
  // Navigate to focus route with thread parameter
  navigate(`/${focus}?${params.toString()}`)
}

/**
 * Build a location string (pathname + search + hash) while preserving `thread`
 * from the current URL if present. For use with `<Link to={...}>` so breadcrumb
 * navigation matches {@link navigateWithThreadPreservation}.
 */
export function getHrefWithThreadPreservation(path: string): string {
  const currentUrlParams = new URLSearchParams(window.location.search)
  const threadId = currentUrlParams.get('thread')

  if (!threadId) {
    return path
  }

  try {
    const url = new URL(path, window.location.origin)
    url.searchParams.set('thread', threadId)
    return url.pathname + url.search + url.hash
  } catch {
    const [pathname, queryAndHash] = path.split('?')
    const [query, hash] = queryAndHash ? queryAndHash.split('#') : ['', '']

    const params = new URLSearchParams(query)
    params.set('thread', threadId)

    return pathname + '?' + params.toString() + (hash ? '#' + hash : '')
  }
}

/**
 * Navigate to a path while preserving thread parameter if it exists.
 * Handles paths with existing query parameters, relative paths, and hash fragments.
 * Reads thread parameter from window.location.search to avoid stale React state.
 *
 * @param navigate - React Router navigate function
 * @param path - The path to navigate to (e.g., '/profile', '/platform/users?q=test')
 * @param searchParams - Current URL search params (kept for compatibility, but we read from window.location.search)
 */
export function navigateWithThreadPreservation(
  navigate: NavigateFunction,
  path: string,
  searchParams: URLSearchParams,
  options?: { state?: unknown },
): void {
  const currentUrlParams = new URLSearchParams(window.location.search)
  const threadId = currentUrlParams.get('thread')

  const finalPath = getHrefWithThreadPreservation(path)

  console.log('🧭 [NAVIGATE WITH THREAD] Navigation request:', {
    path,
    threadIdFromURL: threadId,
    threadIdFromSearchParams: searchParams.get('thread'),
    currentURL: window.location.href,
    usingURLThreadId: !!threadId,
    finalPath,
  })

  if (!threadId) {
    console.log('🧭 [NAVIGATE WITH THREAD] No thread ID found - navigating without thread parameter')
  } else {
    console.log('🧭 [NAVIGATE WITH THREAD] Navigating with thread preserved:', {
      originalPath: path,
      finalPath,
      threadId,
    })
  }

  if (options?.state !== undefined) {
    navigate(finalPath, { state: options.state })
  } else {
    navigate(finalPath)
  }
}

/**
 * Get the current focus from focus store or fallback to default.
 * 
 * @param focus - Focus object from useFocusStore
 * @param defaultFocus - Default focus if none is set (default: 'candidate')
 * @returns Current focus string
 */
export function getCurrentFocus(
  focus: { current_focus: string | null } | null,
  defaultFocus: string = 'candidate'
): string {
  return focus?.current_focus || defaultFocus
}

